import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { ColDef } from 'ag-grid-community';
import { TableConfig } from '../../../shared/components/table/table.interfaces';
import { ActionConfig } from '../../../shared/components/table/table-action-configs';
import {
  ASSET_ALLOCATED_COLUMNS,
  ASSET_ALLOCATED_TABLE_CONFIG,
  ASSET_ALLOCATED_SINGLE_ACTIONS,
} from '../asset-management-table-configuration';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AssetManagementService } from '../asset-management-service';
import { AddNewAssetFormComponent } from '../asset-allocated/add-new-asset-form/add-new-asset-form.component';
import { getMonthDateRange } from '@app/shared/utils/date.utils';
import { yearDateFormat } from '@app/shared/constants/dateFormat';

@Injectable({
  providedIn: 'root',
})
export class AssetAllocatedTableService {
  createAssetRequest(arg0: { assetAction: string; laptopType: any; accessoriesType: string[]; comments: any; fileUrl: string; laptopSerialNumber: any; chargerSerialNumber: any; employeeId: any; }) {
    throw new Error('Method not implemented.');
  }

  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly dataSubject = new BehaviorSubject<any[]>([]);

  public readonly data$ = this.dataSubject.asObservable();
  public readonly loading$ = this.loadingSubject.asObservable();

  public readonly columns: ColDef[] = ASSET_ALLOCATED_COLUMNS;

  public readonly tableConfig: TableConfig = {
    enableRowSelection: ASSET_ALLOCATED_TABLE_CONFIG.enableRowSelection,
    enableActions: ASSET_ALLOCATED_TABLE_CONFIG.enableActions,
    enableSorting: true,
    enableFiltering: true,
    enableExport: true,
  };

  currentStartDate: string = '';
  currentEndDate: string = '';

  public readonly actions: ActionConfig[] = ASSET_ALLOCATED_SINGLE_ACTIONS;
  constructor(
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly assetManagementService: AssetManagementService,
  ) {}

  loadInitialData(): void {
    const { startDate, endDate } = getMonthDateRange(
      history.state.selectedMonth,
      yearDateFormat.date,
    );
    this.currentStartDate = startDate;
    this.currentEndDate = endDate;
    this.getAssetAllocatedData(this.currentStartDate, this.currentEndDate);
  }

  /**
   * Get asset allocated data with date filtering (following vendor-stats pattern)
   * @param startDate - Start date in YYYY-MM-DD format
   * @param endDate - End date in YYYY-MM-DD format
   */
  getAssetAllocatedData(startDate: string, endDate: string): void {
    this.loadingSubject.next(true);

    this.assetManagementService.getAllAssetCardDetails(
      startDate != '' ? startDate : this.currentStartDate,
      endDate != '' ? endDate : this.currentEndDate,
    )
      .subscribe(
        (data) => {
          let processedData = [];
          if (Array.isArray(data)) {
            processedData = data;
          } else if (data && typeof data === 'object' && data.data) {
            processedData = data.data;
          } else if (data && typeof data === 'object') {
            processedData = [data];
          }
          this.dataSubject.next(processedData);
          this.loadingSubject.next(false);
        },
        (error) => {
          this.loadingSubject.next(false);
        }
      );
  }

  refreshData(): void {
    const { startDate, endDate } = getMonthDateRange(
      history.state.selectedMonth,
      yearDateFormat.date,
    );
    this.currentStartDate = startDate;
    this.currentEndDate = endDate;
    this.getAssetAllocatedData(this.currentStartDate, this.currentEndDate);
  }

  openAddNewAssetFormDialog(selectedRecord?: any): void {
    const dialogRef: MatDialogRef<AddNewAssetFormComponent> = this.dialog.open(
      AddNewAssetFormComponent,
      {
        width: '1000px',
        maxWidth: '90vw',
        minHeight: '300px',
        maxHeight: '85vh',
        disableClose: false,
        panelClass: 'my-asset-allocated-modal',
        data: {
          data : selectedRecord || null,
        },
      },
    );

    // Auto-refresh table when dialog closes with successful submission
    dialogRef.afterClosed().subscribe(result => {
      if (result && result.submitted === true) {
        // Refresh the table data after successful submission
        this.refreshData();
      }
    });
  }

  private getDefaultDateRange(): { startDate: string; endDate: string } {
    const today = new Date();
    const priorDate = new Date();
    priorDate.setDate(today.getDate() - 30);
    const startDate = priorDate.toISOString().split('T')[0];
    const endDate = today.toISOString().split('T')[0];
    return { startDate, endDate };
  }

  handleAction(action: string, rowData: any): void {
    this.openAddNewAssetFormDialog(rowData);
    switch (action) {
      case 'view-details':
        break;
    }
  }
}
